import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Typography,
  useTheme
} from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import DashboardImg from "../../../Assets/SVG/undraw_calculator.svg";
import HistoryList from "../HistoryList/HistoryList";
import { useNavigate } from 'react-router';
import { IMsalContext, useMsal } from "@azure/msal-react";
import { ApiService } from "../../../Services/ApiService";
import { IUserSettings } from "../../../Models/IUserSettings";
import { setUserSettings } from "../../../Services/Global";
import logger from '../../../Services/LoggingService';
import { createFlexStyles, createSpacingStyles, createCardStyles } from '../../../theme/layoutStyles';


/**
 * Dashboard component for displaying the dashboard - Material UI version
 *
 * @return {*} 
 */
export const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const flexStyles = createFlexStyles(theme);
  const spacingStyles = createSpacingStyles(theme);
  const cardStyles = createCardStyles(theme);

  const ctx: IMsalContext = useMsal();
  const apiService = useMemo(() => new ApiService(ctx), [ctx]);
  const [isLoading, setIsLoading] = useState(true);

  // Styles
  const styles = {
    container: {
      ...flexStyles.flexColumn,
      height: '100%',
      overflow: 'hidden',
      // margin: '100px',
    },
    header: {
      padding: theme.spacing(2.5), // 20px equivalent
      flexShrink: 0,
    },
    headerTitle: {
      fontSize: '1.5rem',
      fontWeight: 500,
      margin: 0,
      color: theme.palette.text.primary,
    },
    mainSection: {
      ...cardStyles.sectionCard,
      margin: theme.spacing(2.5),
      marginTop: 0,
      marginBottom: 0, // Override default section card margin
      minHeight: '200px',
      flexShrink: 0,
    },
    sectionTitle: {
      fontSize: '1.25rem',
      fontWeight: 500,
      ...spacingStyles.marginBottom2,
      color: theme.palette.text.primary,
    },
    contentContainer: {
      ...flexStyles.flexCenter,
      gap: theme.spacing(3),
    },
    textContent: {
      flex: 1,
      ...flexStyles.flexColumn,
      gap: theme.spacing(2),
    },
    description: {
      fontSize: '1.125rem', // Large font size equivalent
      color: theme.palette.text.primary,
      lineHeight: 1.5,
      margin: 0,
    },
    startButton: {
      marginTop: theme.spacing(3),
      textTransform: 'none' as const,
      fontSize: '1rem',
      padding: theme.spacing(1.5, 3),
      minWidth: '120px',
      '& .MuiButton-endIcon': {
        marginLeft: theme.spacing(1),
      },
    },
    imageContainer: {
      ...flexStyles.flexCenter,
      justifyContent: 'center', // Override for explicit center
      flexShrink: 0,
      marginRight: '-23px',
      marginBottom: '-21px',
    },
    dashboardImage: {
      width: '100%',
      height: '100%',
      marginTop: '-62px', // Maintain the negative margin from original
      
    },
    historySection: {
    //   flex: 1,
    //   overflow: 'hidden',
      ...flexStyles.flexCenter,
    //   flexDirection: 'column' as const,
      margin: theme.spacing(2.5), // Same margin as mainSection
    //   marginTop: 0, // No top margin to maintain spacing
    },
    loadingContainer: {
      ...flexStyles.flexCenter,
      gap: theme.spacing(1),
    },
  };

  useEffect(() => {
    async function getUserSettings() {
      try {
        const response = await apiService.getAsync('settings');
        if (response.status >= 400) {
          const errMsg = `There was an issue getting user settings. Status code: ${response.status}`;
          throw new Error(errMsg);
        }
        const settings: IUserSettings = await response.json();
        setUserSettings(settings);
      } catch (error) {
        logger.error('Failed to fetch user settings', error as Error, null, {
          component: 'Dashboard',
          method: 'getUserSettings'
        });
      } finally {
        setIsLoading(false);
      }
    }

    getUserSettings();
  }, [apiService]);

  const handleStartClick = () => {
    navigate("/design");
  };

  return (
    <Box sx={styles.container}>
      {/* Header */}
      <Box sx={styles.header}>
        <Typography variant="h4" sx={styles.headerTitle}>
          Design
        </Typography>
      </Box>

      {/* Main EMELIE Section */}
      <Paper elevation={1} sx={styles.mainSection}>
        <Typography variant="h5" sx={styles.sectionTitle}>
          EMELIE
        </Typography>

        <Box sx={styles.contentContainer}>
          {/* Text Content */}
          <Box sx={styles.textContent}>
            <Typography sx={styles.description}>
              Start designing the exact heat exchanger for your customer with a few
              parameters.
            </Typography>

            <Box>
              <Button
                variant="contained"
                color="primary"
                endIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <ArrowForwardIcon />}
                disabled={isLoading}
                onClick={handleStartClick}
                sx={styles.startButton}
              >
                {isLoading ? 'Loading...' : 'Start'}
              </Button>
            </Box>
          </Box>

          {/* Image */}
          <Box sx={styles.imageContainer}>
            <Box
              component="img"
              src={DashboardImg}
              alt="dashboard"
              sx={styles.dashboardImage}
            />
          </Box>
        </Box>
      </Paper>

      {/* History List Section */}
      <Box sx={styles.historySection}>
        <HistoryList />
      </Box>
    </Box>
  );
}; 